import qs from "qs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useItemsApi, type BulkApprovalResult } from "./useItemsApi";
import type {
  BiddingInfoItem,
  BiddingInfoSubItem,
  BulkResult,
  ExcelInfoItem,
  GetItemsFilters,
  ManualNumberingResponse,
  BiddingItemResponse
} from "../types"; // 型をインポート
import type { ItemStatusType } from "../constants";
import toast from "react-hot-toast";
import { showApiErrorToast } from "./util/commonMutation";
import type { MutationConfig } from "./util/commonMutation";
import type { FavoriteKeys } from "../types/FavoriteKey";
import { UserRole } from "../types/UserRole";
import { useAuth } from "../contexts/AuthContext";

/**
 * 指定された条件に一致する出品情報一覧を取得します。
 * @param filters 検索フィルター
 * @param loginId ログインユーザーID
 * @returns 出品情報一覧
 */
export const useBiddingItemsGetItemListQuery = (
  filters: GetItemsFilters,
  loginId: string,
) => {
  const { biddingItemsGetItemList } = useItemsApi();
  return useQuery<BiddingItemResponse, Error>({
    // 成功時とエラー時の型を指定
    queryKey: ["biddingitems", "list", filters, loginId],
    // queryFn: async () => {
    //   const params = new URLSearchParams();
    //   if (filters.receptionDate) params.append('receptionDate', filters.receptionDate);
    //   if (filters.receptionDateFrom) params.append('receptionDateFrom', filters.receptionDateFrom);
    //   if (filters.receptionDateTo) params.append('receptionDateTo', filters.receptionDateTo);
    //   if (filters.biddingDate) params.append('biddingDate', filters.biddingDate);
    //   if (filters.biddingDateFrom) params.append('biddingDateFrom', filters.biddingDateFrom);
    //   if (filters.biddingDateTo) params.append('biddingDateTo', filters.biddingDateTo);
    //   if (filters.sellerCode) params.append('sellerCode', String(filters.sellerCode));
    //   if (filters.farmerCode) params.append('farmerCode', String(filters.farmerCode));
    //   if (filters.teaTypeCode) params.append('teaTypeCode', String(filters.teaTypeCode));
    //   if (filters.teaSeasonCode) params.append('teaSeasonCode', String(filters.teaSeasonCode));
    //   if (filters.varietyCode) params.append('varietyCode', String(filters.varietyCode));
    //   if (filters.brand) params.append('brand', filters.brand);
    //   if (filters.listingCategory) params.append('listingCategory', String(filters.listingCategory));
    //   if (filters.status) params.append('status', String(filters.status));
    //   return biddingItemsGetItemList(params);
    // },
    queryFn: async () => {
      // 1. フィルタオブジェクトのクリーンアップ (空文字やnullを除外)
      //    これにより URLSearchParams への手動 append が不要になります
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== ""),
      );

      // 2. クエリパラメータの生成 (qsライブラリを使用)
      //    arrayFormat: 'repeat' を指定することで、
      //    sortKeys: ['A', 'B'] が "sortKeys=A&sortKeys=B" に変換されます
      //    (ASP.NET Core の [FromQuery] List<string> にバインドされる形式)
      const queryString = qs.stringify(cleanFilters, { arrayFormat: "repeat" });

      // 3. API呼び出し
      //    ※ biddingItemsGetItemList が URLSearchParams を受け取る仕様なら修正が必要
      //    ここではクエリ文字列、または params オブジェクトを渡す形を想定
      return biddingItemsGetItemList(queryString);
    },

    // // データを「新鮮(fresh)」と見なす時間を設定します。
    // // この時間内は、画面に戻ってきてもAPIの再実行は行われません。
    // staleTime: 1000 * 30, // 30秒

    // // filtersオブジェクトの値（['', '20250101', null, 123, ...]) を配列として取得し、
    // // その中に1つでも「truthy」な値（空文字、null、undefined、0以外）が存在するかチェック
    // enabled: Object.values(filters).some(Boolean),
    enabled: !!loginId,
  });
};

/**
 * 指定された条件に一致する出品情報一覧を取得します（オプション指定版）。
 * 第3引数で useQuery のオプション (enabled など) を指定可能です。
 * * @param filters 検索フィルター
 * @param loginId ログインユーザーID
 * @param options useQueryのオプション (例: { enabled: boolean })
 * @returns 出品情報一覧
 */
export const useBiddingItemsGetItemListQueryWithControl = (
  filters: GetItemsFilters,
  loginId: string,
  options?: { enabled?: boolean },
) => {
  const { biddingItemsGetItemList } = useItemsApi();

  return useQuery<BiddingItemResponse, Error>({
    queryKey: ["biddingitems", "list", filters, loginId],

    queryFn: async () => {
      // 1. フィルタオブジェクトのクリーンアップ
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== ""),
      );

      // 2. クエリパラメータの生成
      const queryString = qs.stringify(cleanFilters, { arrayFormat: "repeat" });

      // 3. API呼び出し
      return biddingItemsGetItemList(queryString);
    },

    // options.enabled が指定されていればそれを使い、なければ loginId の有無だけで判定
    // これにより、呼び出し元で「まだ呼ばないで！」と制御できるようになります。
    enabled: (options?.enabled ?? true) && !!loginId,
  });
};

/**
 * 指定された条件に一致する単一の出品情報を取得します。
 * @param itemId 出品ID
 * @param branchNumber 枝番
 * @returns 出品情報
 */
export const useBiddingItemsGetItemQuery = (
  itemId: string,
  branchNumber: number,
) => {
  const { biddingItemsGetItem } = useItemsApi();
  return useQuery<BiddingInfoItem | null, Error>({
    queryKey: ["biddingitems", itemId, branchNumber],
    queryFn: async () => {
      return biddingItemsGetItem(itemId, branchNumber);
    },
    enabled: !!itemId && !!branchNumber,
  });
};

/**
 * 出品アイテム（出品メインアイテム＋出品サブアイテム）を１件新規登録します。
 * ※ 出品サブアイテムは出品メインアイテムをもとに１件登録
 * @returns 作成された出品メイン情報
 */
export const useCreateBiddingItemMutation = () => {
  const SUCCESS_MESSAGE = "新しい出品情報を登録しました。"; // 成功時メッセージ
  const ERROR_MESSAGE = "登録に失敗しました。"; // エラー時メッセージ
  const QUERY_KEY = { queryKey: ["biddingitems", "list"] }; // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    BiddingInfoItem | null, // ret (TData)
    Error, // error (TError)
    { newItemData: BiddingInfoItem } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { createBiddingItem } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) => createBiddingItem(variables.newItemData),

    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },

    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });

  // ★下記のようにも書ける
  // return useMutation
  // ({
  //   mutationFn: (newItemData: BiddingInfoItem) => createBiddingItem(newItemData),
  //   onSuccess: () => {
  //     toast.success('新しい出品情報を登録しました。');
  //     // 関連する一覧データのキャッシュを無効化し、再取得を促す
  //     queryClient.invalidateQueries({ queryKey: ['biddingitems', 'list'] });
  //   },
  //   onError: (error) => showApiErrorToast(error, "登録に失敗しました。"),
  // });
};

/**
 * 出品IDに紐づく出品メインアイテムを１件更新します。
 * @returns 更新後の出品メイン情報
 */
export const useUpdateBiddingItemMutation = () => {
  const SUCCESS_MESSAGE = "出品情報を更新しました。"; // 成功時メッセージ
  const ERROR_MESSAGE = "更新に失敗しました。"; // エラー時メッセージ
  const QUERY_KEY = { queryKey: ["biddingitems", "list"] }; // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    boolean, // ret (TData)
    Error, // error (TError)
    { updateItemData: BiddingInfoItem } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { updateBiddingItem } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) => updateBiddingItem(variables.updateItemData),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 *  出品IDに紐づく出品アイテム（出品メインアイテム＋出品サブアイテム全件）を削除します。
 * @returns
 */
export const useDeleteBiddingItemMutation = () => {
  const SUCCESS_MESSAGE = "出品情報を削除しました。"; // 成功時メッセージ
  const ERROR_MESSAGE = "出品情報の削除に失敗しました。"; // エラー時メッセージ
  const QUERY_KEY = { queryKey: ["biddingitems", "list"] }; // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    boolean, // ret (TData)
    Error, // error (TError)
    { itemId: string } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { deleteBiddingItem } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) => deleteBiddingItem(variables.itemId),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * 複数の出品サブアイテムを一括で新規登録します。
 * ※ 出品IDに紐づく既存の出品サブアイテムは全件削除した後にリクエスト内容で新規登録します
 * ※ リクエスト0件の場合は出品メインアイテムをもとに出品サブアイテムを１件登録
 * @returns 更新後の出品サブ情報一覧
 */
export const useUpsertSubItemsMutation = () => {
  const SUCCESS_MESSAGE = "出品サブ情報を更新しました。"; // 成功時メッセージ
  const ERROR_MESSAGE = "出品サブ情報の更新に失敗しました。"; // エラー時メッセージ
  const QUERY_KEY = { queryKey: ["biddingitems", "list"] }; // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    BiddingInfoItem, // ret (TData)
    Error, // error (TError)
    { itemId: string; requests: BiddingInfoSubItem[] } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { upsertSubItems } = useItemsApi(); // API呼び出し関数を取得

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      upsertSubItems(variables.itemId, variables.requests),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * 出品IDに紐づく出品情報ステータスを更新します。
 * @returns
 */
export const useUpdateBiddingItemStatusMutation = () => {
  const SUCCESS_MESSAGE = "出品情報のステータスを更新しました。"; // 成功時メッセージ
  const ERROR_MESSAGE = "出品情報のステータス更新に失敗しました。"; // エラー時メッセージ
  const QUERY_KEY = { queryKey: ["biddingitems", "list"] }; // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    boolean, // ret (TData)
    Error, // error (TError)
    { itemId: string; status: ItemStatusType } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { updateBiddingItemStatus } = useItemsApi();
  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      updateBiddingItemStatus(variables.itemId, variables.status),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * 出品情報のステータスを一括更新します。
 * @returns
 */
export const useBulkUpdateBiddingItemStatusMutation = () => {
  const SUCCESS_MESSAGE = "出品情報のステータスを一括更新しました。"; // 成功時メッセージ
  const ERROR_MESSAGE = "出品情報のステータスの一括更新に失敗しました。"; // エラー時メッセージ
  const QUERY_KEY = { queryKey: ["biddingitems", "list"] }; // 処理後無効化するキャッシュのクエリキー

  type mutationConfig = MutationConfig<
    boolean, // ret (TData)
    Error, // error (TError)
    { targetStatus: ItemStatusType; itemIds: string[] } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { bulkUpdateBiddingItemStatus } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      bulkUpdateBiddingItemStatus(variables.targetStatus, variables.itemIds),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * 出品情報の一括登録
 */
export const useBulkCreateBiddingItemMutation = () => {
  const SUCCESS_MESSAGE = "一括登録処理が完了しました。";  // 成功時メッセージ
  const ERROR_MESSAGE = "一括登録に失敗しました。";  // エラー時メッセージ
  const QUERY_KEY = { queryKey: ['bulkbiddingitems', 'list'] } // 処理後無効化するキャッシュのクエリキー (必要？)

  type mutationConfig = MutationConfig<
    BulkResult | null, // ret (TData)
    Error,                  // error (TError)
    { newItemDataList: ExcelInfoItem[], prefectureType: number } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { bulkCreateBiddingItem } = useItemsApi();

  return useMutation<mutationConfig['ret'], mutationConfig['error'], mutationConfig['variables']> ({
    mutationFn: (variables) => bulkCreateBiddingItem(variables.newItemDataList, variables.prefectureType),

    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE, 
      {
        duration: 5000, //メッセージ表示時間
      });
      // 関連する一覧データのキャッシュを無効化し、再取得を促す
      queryClient.invalidateQueries(QUERY_KEY);
    },

    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * 枝番単位の承認ステータスを単一更新します。
 */
export const useUpdateSubItemApprovalStatusMutation = () => {
  const SUCCESS_MESSAGE = "落札確定承認状態を更新しました。";
  const ERROR_MESSAGE = "落札確定承認状態の更新に失敗しました。";

  // 影響を受けるすべてのクエリを再取得させるためのキー
  const QUERY_KEYS = [
    { queryKey: ["biddingitems"] },
    { queryKey: ["biddinginfo"] },
    { queryKey: ["biddingResultConfirm"] },
  ];

  type mutationConfig = MutationConfig<
    BiddingInfoSubItem, // ret (TData)
    Error, // error (TError)
    { itemId: string; branchNumber: number; approvalStatus: number } // variables (TVariables)
  >;

  const queryClient = useQueryClient();
  const { updateSubItemApprovalStatus } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      updateSubItemApprovalStatus(
        variables.itemId,
        variables.branchNumber,
        variables.approvalStatus,
      ),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 一覧画面と結果画面の両方のキャッシュを無効化
      QUERY_KEYS.forEach((key) => queryClient.invalidateQueries(key));
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * サブアイテムの落札確定承認ステータスを一括更新します。
 */
export const useBulkUpdateSubItemApprovalStatusMutation = (
  filters: GetItemsFilters,
  loginId: string
) => {
  const SUCCESS_MESSAGE = "落札確定承認状態を一括更新しました。";
  const ERROR_MESSAGE = "落札確定承認状態の一括更新に失敗しました。";

  const QUERY_KEYS = [
    { queryKey: ["biddingResultConfirm", filters, loginId] },
  ];

  type mutationConfig = MutationConfig<
    BulkApprovalResult,
    Error,
    {
      targets: { itemId: string; branchNumber: number }[];
      approvalStatus: number;
      pageName: string;
    }
  >;

  const queryClient = useQueryClient();
  const { bulkUpdateSubItemApprovalStatus } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      bulkUpdateSubItemApprovalStatus(
        variables.targets,
        variables.approvalStatus,
        variables.pageName,
      ),
    onSuccess: (data) => {
      // per-item 失敗は HTTP 200 のまま errors に明細で返る（BulkApprovalResultDto）。
      // 従来は応答を見ず常に成功トーストだったため、全滅でも「成功」と表示されていた（BUGS-03 所見2）。
      const failureCount = data?.errors?.length ?? 0;
      if (failureCount > 0) {
        const detail = data.errors[0]?.errorMessage ?? "";
        toast.error(
          `落札確定承認: ${data.successCount}件成功・${failureCount}件失敗しました。${detail ? `（${detail}）` : ""}`,
          { duration: 8000 },
        );
      } else {
        toast.success(SUCCESS_MESSAGE);
      }
      QUERY_KEYS.forEach((key) => queryClient.invalidateQueries(key));
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * サブアイテムの最低落札値を単一更新します。
 */
export const useUpdateSubItemBidMinPriceMutation = () => {
  const SUCCESS_MESSAGE = "最低落札値を更新しました。";
  const ERROR_MESSAGE = "最低落札値の更新に失敗しました。";

  // 影響を受けるすべてのクエリを再取得させるためのキー
  const QUERY_KEYS = [
    { queryKey: ["biddingitems"] },
    { queryKey: ["biddinginfo"] },
  ];

  type mutationConfig = MutationConfig<
    BiddingInfoSubItem, // ret (TData)
    Error, // error (TError)
    { itemId: string; branchNumber: number; bidMinPrice: number }
  >;

  const queryClient = useQueryClient();
  const { updateSubItemBidMinPrice } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      updateSubItemBidMinPrice(
        variables.itemId,
        variables.branchNumber,
        variables.bidMinPrice,
      ),
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGE);
      // 一覧画面等のキャッシュを無効化して再取得させる
      QUERY_KEYS.forEach((key) => queryClient.invalidateQueries(key));
    },
    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};

/**
 * お気に入りの登録・解除を切り替えます（トグル）。
 * @param loginId ログインユーザーID（キャッシュの無効化に使用）
 */
export const useToggleFavoriteMutation = (loginId: string) => { // ★ loginId を受け取るように変更
  const queryClient = useQueryClient();
  const { toggleFavorite } = useItemsApi();

  // プロジェクト独自の MutationConfig 型定義
  type mutationConfig = MutationConfig<
    { isFavorite: boolean },
    Error,
    { itemId: string; branchNumber: number }
  >;

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      toggleFavorite(variables.itemId, variables.branchNumber),

    onSuccess: (data) => {
      // 1. メッセージの表示
      const message = data.isFavorite
        ? "お気に入りに追加しました。"
        : "お気に入りから削除しました。";
      toast.success(message);

      // 2. ★プランBの重要ポイント：お気に入りリストのキャッシュを無効化する
      // これにより、useGetFavoritesQuery が再実行され、画面のハートの色が同期されます。
      queryClient.invalidateQueries({ queryKey: ["favorites", loginId] });

      // もし他の画面（詳細画面など）でもお気に入り状態を表示しているなら、
      // 念のため既存のキーも無効化しておくと安全です
      queryClient.invalidateQueries({ queryKey: ["biddinginfo"] });
    },

    onError: (error) => showApiErrorToast(error, "お気に入り操作に失敗しました。"),
  });
};

/**
 * 現在のユーザーのお気に入りリストを取得します。
 */
export const useGetFavoritesQuery = (loginId: string, options?: any) => {
  const { getFavorites } = useItemsApi();
  const { currentUser } = useAuth(); // ★ フック内で認証情報を取得

  // ★ ユーザーが「買い手(Buyer)」かどうかを判定
  const isBuyer = currentUser?.role === UserRole.Buyer;

  return useQuery<FavoriteKeys[], Error>({
    queryKey: ['favorites', loginId],
    queryFn: getFavorites,
    // ★ ガード条件：
    // 1. 外部から enabled が指定されていればそれに従う
    // 2. 指定がなければ「ログインIDがあり」かつ「買い手である」場合のみ実行
    enabled: options?.enabled !== undefined 
      ? options.enabled 
      : (!!loginId && isBuyer), 
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

/**
 * 手動で入札Noを採番します。
 */
export const useUpdateBiddingNumberMutation = () => {
  const ERROR_MESSAGE = "手動採番に失敗しました。";

  type mutationConfig = MutationConfig<
    ManualNumberingResponse, // TData
    Error,                   // TError
    { 
      biddingDate: number; 
      listingCategory: number; 
      prefectureType: number 
    } // TVariables (オブジェクト形式)
  >;

  const { updateBiddingNumber } = useItemsApi();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationFn: (variables) =>
      updateBiddingNumber(
        variables.biddingDate,
        variables.listingCategory,
        variables.prefectureType
      ),

    onSuccess: (data) => {
      if (data.isSuccess) {
        toast.success(`処理が正常に完了しました。\n${data.count}件更新しました。`, {
          duration: 5000,
        });
      } else {
        // APIは成功(200)だが、ロジックエラーの場合
        toast.error(data.message || ERROR_MESSAGE, {duration: 5000});
      }
    },

    onError: (error) => showApiErrorToast(error, ERROR_MESSAGE),
  });
};